// Production → Time Study (Research). Plan: docs/planning/mfg_time_study_research.md

import { useCallback, useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { useAuth } from "@/contexts/AuthContext";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { TimeStudyDictateModal } from "./timeStudy/TimeStudyDictateModal";
import {
  TimeStudyEntryForm,
  type TimeStudyEntryFormValues,
} from "./timeStudy/TimeStudyEntryForm";
import { TimeStudySummaries } from "./timeStudy/TimeStudySummaries";
import { listManufacturingBuildModels } from "@/lib/manufacturing/cogsClient";
import { listManufacturingRecipes } from "@/lib/manufacturing/recipesClient";
import {
  closeTimeStudySession,
  createTimeStudyEntry,
  createTimeStudySession,
  deleteTimeStudyEntry,
  listTimeStudyEntries,
  listTimeStudySessions,
  type TimeStudyEntry,
  type TimeStudySession,
} from "@/lib/manufacturing/timeStudyClient";
import { formatDurationSeconds } from "@/lib/manufacturing/timeStudyFormat";
import { listManufacturingPlants } from "@/lib/manufacturingClient";
import { MFG_TIME_STUDY_WRITE_ROLES } from "@/lib/roles/turboManufacturingRoles";

export function ManufacturingTimeStudyPanel(props: {
  embedded?: boolean;
  onSessionActiveChange?: (active: boolean) => void;
} = {}) {
  const gate = useManufacturingEntitlementGate();
  const auth = useAuth();
  const canWrite =
    auth.status === "ready" && auth.hasRole(...MFG_TIME_STUDY_WRITE_ROLES);

  const [sessions, setSessions] = useState<TimeStudySession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string>("");
  const [entries, setEntries] = useState<TimeStudyEntry[]>([]);
  const [recipes, setRecipes] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [buildModels, setBuildModels] = useState<
    Array<{ id: string; name: string; model_code?: string; flat_labor_rate_usd: number }>
  >([]);
  const [plants, setPlants] = useState<Array<{ id: string; name: string; plant_code?: string }>>(
    [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [showDictate, setShowDictate] = useState(false);

  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;

  useEffect(() => {
    props.onSessionActiveChange?.(activeSession?.status === "open");
  }, [activeSession?.status, props.onSessionActiveChange]);

  const refreshSessions = useCallback(async () => {
    const res = await listTimeStudySessions();
    if (!res.ok || !res.data.ok) {
      setError("error" in res ? String(res.error) : "Failed to load sessions");
      return;
    }
    setSessions(res.data.sessions);
    setActiveSessionId((prev) => {
      if (prev && res.data.sessions.some((s) => s.id === prev)) return prev;
      const open = res.data.sessions.find((s) => s.status === "open");
      return open?.id ?? res.data.sessions[0]?.id ?? "";
    });
  }, []);

  const refreshEntries = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      setEntries([]);
      return;
    }
    const res = await listTimeStudyEntries(sessionId);
    if (!res.ok || !res.data.ok) {
      setError("error" in res ? String(res.error) : "Failed to load entries");
      return;
    }
    setEntries(res.data.entries);
  }, []);

  useEffect(() => {
    if (!gate.enabled) return;
    void (async () => {
      setBusy(true);
      setError(null);
      try {
        await refreshSessions();
        const [rRes, mRes, pRes] = await Promise.all([
          listManufacturingRecipes(),
          listManufacturingBuildModels({ is_active: true }),
          listManufacturingPlants(),
        ]);
        if (rRes.ok && rRes.data.ok) {
          setRecipes(
            rRes.data.recipes.map((r) => ({
              id: r.id,
              name: r.name,
              code: r.code,
            })),
          );
        }
        if (mRes.ok && mRes.data.ok) {
          setBuildModels(
            mRes.data.models.map((m) => ({
              id: m.id,
              name: m.name,
              model_code: m.model_code,
              flat_labor_rate_usd: m.flat_labor_rate_usd,
            })),
          );
        }
        if (pRes.ok && pRes.data.ok) {
          setPlants(
            pRes.data.plants.map((p) => ({
              id: p.id,
              name: p.name,
              plant_code: p.plant_code,
            })),
          );
        }
      } finally {
        setBusy(false);
      }
    })();
  }, [gate.enabled, refreshSessions]);

  useEffect(() => {
    if (!gate.enabled || !activeSessionId) return;
    void refreshEntries(activeSessionId);
  }, [gate.enabled, activeSessionId, refreshEntries]);

  const startSession = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await createTimeStudySession({
        title: newTitle.trim() || undefined,
      });
      if (!res.ok || !res.data.ok) {
        setError("error" in res ? String(res.error) : "Could not create session");
        return;
      }
      setNewTitle("");
      await refreshSessions();
      setActiveSessionId(res.data.session.id);
    } finally {
      setBusy(false);
    }
  };

  const closeActive = async () => {
    if (!activeSessionId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await closeTimeStudySession(activeSessionId);
      if (!res.ok) {
        setError("error" in res ? String(res.error) : "Could not close session");
        return;
      }
      await refreshSessions();
    } finally {
      setBusy(false);
    }
  };

  const addEntry = async (values: TimeStudyEntryFormValues) => {
    if (!activeSessionId) {
      setError("Start or select a session first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createTimeStudyEntry({
        session_id: activeSessionId,
        ...values,
        source: "manual",
      });
      if (!res.ok) {
        setError("error" in res ? String(res.error) : "Could not save entry");
        return;
      }
      await refreshEntries(activeSessionId);
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (entryId: string) => {
    if (!canWrite) return;
    setBusy(true);
    setError(null);
    try {
      const res = await deleteTimeStudyEntry(entryId);
      if (!res.ok) {
        setError("error" in res ? String(res.error) : "Could not delete");
        return;
      }
      await refreshEntries(activeSessionId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full space-y-4 text-left">
      {props.embedded ? null : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Time Study</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
              Research — capture labor duration by product or free-form work
            </p>
          </div>
        </div>
      )}

      {props.embedded ? null : <ManufacturingGateBanner gate={gate} />}

      {gate.enabled ? (
        <>
          {error ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          ) : null}

          {!canWrite ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              Read-only: plant floor role can view sessions. Creating or editing requires desk,
              supervisor, or plant manager.
            </p>
          ) : null}

          <SectionCard title="Sessions">
            <div className="mb-3 flex flex-wrap items-end gap-2">
              <label className="block text-sm text-gray-700 dark:text-zinc-300">
                Active session
                <select
                  className={`${inputClass} mt-1 min-w-[14rem]`}
                  value={activeSessionId}
                  onChange={(e) => setActiveSessionId(e.target.value)}
                  disabled={busy || sessions.length === 0}
                >
                  {sessions.length === 0 ? <option value="">No sessions</option> : null}
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.status === "open" ? "● " : "○ "}
                      {s.title || s.id.slice(0, 8)}
                    </option>
                  ))}
                </select>
              </label>
              {canWrite ? (
                <>
                  <label className="block text-sm text-gray-700 dark:text-zinc-300">
                    New session title
                    <input
                      className={`${inputClass} mt-1 min-w-[12rem]`}
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="Optional"
                      disabled={busy}
                    />
                  </label>
                  <button
                    type="button"
                    className={mfgPrimaryBtnClass}
                    disabled={busy}
                    onClick={() => void startSession()}
                  >
                    Start session
                  </button>
                  {activeSession?.status === "open" ? (
                    <button
                      type="button"
                      className={mfgSecondaryBtnClass}
                      disabled={busy}
                      onClick={() => void closeActive()}
                    >
                      Close session
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
            {activeSession ? (
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                {activeSession.status === "open" ? "Open" : "Closed"} · started{" "}
                {new Date(activeSession.started_at).toLocaleString()}
              </p>
            ) : null}
          </SectionCard>

          {canWrite && activeSession?.status === "open" ? (
            <SectionCard
              title="Add observation"
              rightSlot={
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={busy}
                  onClick={() => setShowDictate(true)}
                >
                  Dictate
                </button>
              }
            >
              <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
                Only subject and duration are required. Plant, personnel, quantity, and notes are
                optional.
              </p>
              <TimeStudyEntryForm
                recipes={recipes}
                buildModels={buildModels}
                plants={plants}
                busy={busy}
                onSubmit={addEntry}
              />
            </SectionCard>
          ) : null}

          <SectionCard title="Observations">
            {entries.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-400">No observations in this session.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                {entries.map((e) => (
                  <li
                    key={e.id}
                    className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm"
                  >
                    <div>
                      <div className="font-medium text-gray-900 dark:text-zinc-100">
                        {e.subject_label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-zinc-400">
                        {formatDurationSeconds(e.duration_seconds)}
                        {e.personnel_label ? ` · ${e.personnel_label}` : ""}
                        {e.source === "dictation" ? " · dictated" : ""}
                      </div>
                    </div>
                    {canWrite ? (
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        disabled={busy}
                        onClick={() => void removeEntry(e.id)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {activeSession ? (
            <TimeStudySummaries
              sessionTitle={activeSession.title}
              entries={entries}
              buildModels={buildModels}
            />
          ) : null}

          {showDictate && activeSessionId ? (
            <TimeStudyDictateModal
              sessionId={activeSessionId}
              onClose={() => setShowDictate(false)}
              onSaved={() => void refreshEntries(activeSessionId)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
